import apiClient from './config';
import { handleApiError } from './utils';
import { Cart } from '../types/cart';

/**
 * 최소 주문 금액 미달 시 백엔드가 내려주는 에러 코드
 * (장바구니 생성/주문 API가 HTTP 400과 함께 반환)
 */
export const MIN_ORDER_AMOUNT_NOT_MET_CODE = 'MIN_ORDER_AMOUNT_NOT_MET';

/**
 * 영업을 종료(폐업)한 매장에 대해 백엔드가 내려주는 에러 코드
 * (주문 금액 계산/주문 생성 API가 반환)
 */
export const SHOP_PERMANENTLY_CLOSED_CODE = 'SHOP_PERMANENTLY_CLOSED';

// 장바구니 생성 인터페이스
export interface CreateCartRequest {
  addressId: string;
  service_user_id?: string;
  origin_stay_id?: string;
  deeFranchiseStoreId: string;
  restaurantName: string;
  languageCode: string;
  reservationDateTime?: string; // ISO string 형태의 예약 시간
  items: {
    franchiseMenuId: string;
    name: string;
    price: number;
    quantity: number;
    options?: {
      franchiseMenuOptionId: string;
      name: string;
      price: number;
    }[];
  }[];
}

// 장바구니 응답 인터페이스
export interface CartResponse {
  id: string;
  memberId: string | null;
  addressId: string;
  orderNote: string | null;
  restaurant: {
    id: number;
    name: string;
  };
  items: {
    id?: number;
    menuId: number;
    name: string;
    price: number;
    quantity: number;
    options: {
      id?: number;
      optionId: number;
      name: string;
      price: number;
      optionItems: {
        id?: number;
        optionItemId: number;
        name: string;
        price: number;
      }[];
    }[];
    itemTotalPrice: number;
  }[];
  totalPrice: number;
  orderId: string | null;
  deliveryFee: number;
  lang: string;
  subtotal: number;
  createdDate: string;
  updatedDate: string;
  reservationDateTime?: string; // 예약 배달 시간 (LocalDateTime 형식)
}

// 주문 금액 계산 요청 인터페이스
export interface CalculateOrderRequest {
  addressId: string;
  deeFranchiseStoreId: string;
  languageCode: string;
  items: {
    franchiseStoreMenuId: string;
    quantity: number;
    options?: {
      franchiseStoreMenuOptionId: string;
    }[];
  }[];
}

// 기념품 주문 금액 계산 요청 인터페이스
export interface CalculateOrderGiftRequest {
  addressId: string;
  deeStoreId: string;
  languageCode: string;
  items: {
    giftMenuId: string;
    quantity: number;
  }[];
}

// 주문 금액 계산 응답 인터페이스
export interface CalculateOrderResponse {
  subtotal: number;
  deliveryFee: number;
  totalPrice: number;
  languageCode: string;
  subtotalKrw: number;
  subtotalUsd: number;
  subtotalJpy: number;
  subtotalCny: number;
  subtotalTwd: number;
  deliveryFeeKrw: number;
  deliveryFeeUsd: number;
  deliveryFeeJpy: number;
  deliveryFeeCny: number;
  deliveryFeeTwd: number;
  totalPriceKrw: number;
  totalPriceUsd: number;
  totalPriceJpy: number;
  totalPriceCny: number;
  totalPriceTwd: number;
  promotionRatio: number;
  isPromotionUsd: boolean;
  isPromotionJpy: boolean;
  isPromotionCny: boolean;
  isPromotionTwd: boolean;
  promotionDeliveriesUsd: number;
  promotionDeliveriesJpy: number;
  promotionDeliveriesCny: number;
  promotionDeliveriesTwd: number;
  promotionTotalPriceUsd: number;
  promotionTotalPriceJpy: number;
  promotionTotalPriceCny: number;
  promotionTotalPriceTwd: number;
  promotionSource?: 'STAY_AUTO' | 'USER_CODE' | 'NONE';
  appliedPromotionId?: string | null;
  appliedPolicyId?: string | null;
  // 환율 정보
  exchangeRateUsd?: number;
  exchangeRateCny?: number;
  // 실효 최소주문금액 관련 (구버전 서버 호환을 위해 optional)
  minOrderAmount?: number;        // 현재 언어 통화 기준 실효 최소주문금액
  minOrderAmountKrw?: number;     // 원화 기준 실효 최소주문금액
  minOrderAmountUsd?: number;
  minOrderAmountJpy?: number;
  minOrderAmountCny?: number;
  minOrderAmountTwd?: number;
  isMinOrderAmountMet?: boolean;  // 상품 소계(옵션·배송비 제외)가 최소금액 이상이면 true
}

/**
 * 로컬 장바구니를 주문 금액 계산 요청 형식으로 변환
 */
export const transformCartToCalculateRequest = (
  cart: Cart,
  addressId: string,
  languageCode: string = 'EN'
): CalculateOrderRequest => {
  return {
    addressId,
    deeFranchiseStoreId: String(cart.restaurantId),
    languageCode,
    items: cart.items.map(item => ({
      franchiseStoreMenuId: item.id,
      quantity: item.quantity,
      options: item.options.map(option => ({
        franchiseStoreMenuOptionId: option.id,
      }))
    }))
  };
};

/**
 * 기념품 장바구니를 기념품 주문 금액 계산 요청 형식으로 변환
 */
export const transformCartToCalculateGiftRequest = (
  cart: Cart,
  addressId: string,
  languageCode: string = 'KO'
): CalculateOrderGiftRequest => {
  return {
    addressId,
    deeStoreId: String(cart.restaurantId),
    languageCode,
    items: cart.items.map(item => ({
      giftMenuId: item.id,
      quantity: item.quantity,
    }))
  };
};

/**
 * 주문 금액 계산 API
 * @param calculateData 주문 금액 계산 요청 데이터
 * @param code 프로모션 사용자 ID (선택사항)
 * @returns 계산된 주문 금액 정보
 */
export const calculateOrder = async (calculateData: CalculateOrderRequest, code?: string): Promise<CalculateOrderResponse> => {
  try {
    // 쿼리 파라미터 구성
    const params = new URLSearchParams();
    if (code) {
      params.append('code', code);
    }
    
    const url = params.toString() ? `/api/v1/stores/calculate-order?${params.toString()}` : '/api/v1/stores/calculate-order';
    const response = await apiClient.post<CalculateOrderResponse>(url, calculateData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * 기프트샵 주문 금액 계산 API
 * @param calculateData 기념품 주문 금액 계산 요청 데이터
 * @param code 프로모션 사용자 ID (선택사항)
 * @returns 계산된 주문 금액 정보
 */
export const calculateOrderGift = async (calculateData: CalculateOrderGiftRequest, code?: string): Promise<CalculateOrderResponse> => {
  try {
    // 쿼리 파라미터 구성
    const params = new URLSearchParams();
    if (code) {
      params.append('code', code);
    }
    
    const url = params.toString() ? `/api/v1/stores/calculate-order-gift?${params.toString()}` : '/api/v1/stores/calculate-order-gift';
    const response = await apiClient.post<CalculateOrderResponse>(url, calculateData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * 로컬 장바구니 아이템을 API 요청 형식으로 변환
 */
export const transformCartToRequest = (
  cart: Cart,
  addressId: string,
  languageCode: string = 'EN',
  reservationDateTime?: string,
  serviceUserId?: string | null,
  originStayId?: string | null
): CreateCartRequest => {
  return {
    addressId,
    ...(serviceUserId && { service_user_id: serviceUserId }),
    ...(originStayId && { origin_stay_id: originStayId }),
    deeFranchiseStoreId: String(cart.restaurantId),
    restaurantName: cart.restaurantName,
    languageCode,
    ...(reservationDateTime && { reservationDateTime }),
    items: cart.items.map(item => {
      return {
        franchiseMenuId: item.id,
        name: item.name,
        price: item.price, // 로컬스토리지의 원본 가격 그대로 사용
        quantity: item.quantity,
        options: item.options.map(option => {
          return {
            franchiseMenuOptionId: option.id,
            name: option.name,
            price: option.price, // 옵션 가격도 원본 그대로 사용
          };
        })
      };
    })
  };
};

/**
 * 장바구니 생성 API
 * @param cartData 장바구니 생성 요청 데이터
 * @returns 생성된 장바구니 정보
 */
export const createCart = async (cartData: CreateCartRequest): Promise<CartResponse> => {
  try {
    const response = await apiClient.post<CartResponse>('/api/carts', cartData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * 장바구니 조회 API
 * @param cartId 장바구니 ID
 * @param languageCode 언어 코드 (선택사항)
 * @returns 장바구니 정보
 */
export const getCart = async (cartId: string, languageCode?: string): Promise<CartResponse> => {
  try {
    // 언어 코드가 있으면 쿼리 파라미터로 추가
    const url = languageCode 
      ? `/api/carts/${cartId}?lang=${languageCode}`
      : `/api/carts/${cartId}`;
    
    const response = await apiClient.get<CartResponse>(url);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * 기념품 장바구니 생성 API
 * @param cartData 기념품 장바구니 생성 요청 데이터 (기존 CreateCartRequest와 동일한 구조)
 * @returns 생성된 장바구니 정보
 */
export const createCartGift = async (cartData: CreateCartRequest): Promise<CartResponse> => {
  try {
    const response = await apiClient.post<CartResponse>('/api/carts/gift', cartData);
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}; 